// Die Karte — eine Phaser-Szene im Ausschnitt des Panels.
//
// Dieselbe Zweiteilung wie beim Technologiebaum, und aus demselben Grund: Text
// und Listen sind im DOM billiger, ein Ringdiagramm mit Marken gehoert auf
// Canvas. Und dieselben zwei Riegel — sichtbar schalten reicht nicht, die
// Szene muss auch wissen, ob ihr Reiter offen ist (`setOpen`), sonst nimmt sie
// Zeiger entgegen, waehrend niemand hinsieht (`oberflaeche.md`).
//
// **Sie rechnet nichts selbst.** Abbildung, Marken und Ringe stehen in
// `sim/mapview.ts`; hier steht nur, wie sie aussehen. Eine Karte, die im
// Zeichenpfad rechnet, zeigt woanders hin, ohne dass etwas scheitert.

import Phaser from 'phaser';

import { bus } from '../core/bus';
import { distStr } from '../core/format';
import { balancing, planetById } from '../data/balancing';
import { cssColor } from '../gfx/palette';
import { mapMarks, mapScale, markAt, zoneRings, type MapMark } from '../sim/mapview';
import { builtIds } from '../sim/stats';
import type { Point } from '../sim/sidequests';
import { store } from '../state/store';

/** Was die Karte nach aussen meldet. Sie entscheidet nichts selbst. */
export interface MapCallbacks {
  /** Der Spieler hat eine Marke gewaehlt — die Verdrahtung setzt den Kurs. */
  onPick(mark: MapMark): void;
  /** Ein freier Punkt auf der Karte. */
  onPickPoint(x: number, y: number): void;
}

const NO_CALLBACKS: MapCallbacks = { onPick: () => {}, onPickPoint: () => {} };

/** Farben der Karte. Sie stehen bei den Formen, wie bei den Bauteilen. */
const MAP_BG = '#0b1016';
const MAP_RING = 0x2c3a46;
const MAP_WALL = 0x6a3a3a;
const MAP_BASE = 0x5fe6c8;
const MAP_SHIP = 0xe8e2d4;
const MAP_SIDE = 0xffd84d;
const MAP_COURSE = 0x8ce05a;

/** Trefferradius eines Tipps in **Bildpunkten** — ein Finger kennt kein Zeigen. */
const PICK_PIXELS = 22;

export class MapScene extends Phaser.Scene {
  static readonly KEY = 'map';

  private gfx!: Phaser.GameObjects.Graphics;
  private label!: Phaser.GameObjects.Text;
  private callbacks: MapCallbacks = NO_CALLBACKS;
  private open = false;

  /** Wiederverwendete Puffer — die Karte zeichnet je Bild (Gate FG-14). */
  private readonly marks: MapMark[] = [];
  private readonly rings: number[] = [];
  private readonly ship: Point = { x: 0, y: 0 };
  private course: Point | null = null;
  private seed = 0;
  /** Woher die Karte ihren Massstab nimmt — Mittelpunkt und Bildpunkte je Einheit. */
  /** Bildpunkte je Welteinheit. Nicht `scale` — das ist Phasers ScaleManager. */
  private pxPer = 1;
  private cx = 0;
  private cy = 0;

  constructor() {
    super({ key: MapScene.KEY, active: false });
  }

  setCallbacks(callbacks: MapCallbacks): void {
    this.callbacks = callbacks;
  }

  /**
   * Die Karte nimmt Zeiger nur entgegen, solange ihr Reiter offen ist.
   *
   * Derselbe Riegel wie beim Technologiebaum: `scene.setVisible(false)`
   * schaltet nur das **Zeichnen** ab. Ohne ihn setzte ein Klick auf die Welt
   * einen Kurs, den niemand gesehen hat.
   */
  setOpen(open: boolean): void {
    this.open = open;
    if (!open) this.label.setText('');
  }

  /** Woher die Karte ihre Marken rechnet — Saat, Schiffsort, laufender Kurs. */
  setWorld(seed: number, x: number, y: number, course: Point | null): void {
    this.seed = seed;
    this.ship.x = x;
    this.ship.y = y;
    this.course = course;
  }

  create(): void {
    this.gfx = this.add.graphics().setDepth(0);
    // Deckend: der Kamera-Ausschnitt **ist** die Flaeche des Panels, und das
    // DOM darueber laesst sie bewusst frei (siehe `style.css`).
    this.cameras.main.setBackgroundColor(MAP_BG);
    this.label = this.add.text(8, 8, '', {
      fontFamily: '"Lucida Console", monospace',
      fontSize: '12px',
      color: '#cdc6b8',
    }).setDepth(2).setScrollFactor(0);
    this.scene.setVisible(false);
    this.bindInput();
  }

  /** Den Zeichenbereich an den Panel-Ausschnitt binden. */
  setViewportRect(rect: DOMRect): void {
    this.cameras.main.setViewport(
      Math.round(rect.left), Math.round(rect.top),
      Math.round(rect.width), Math.round(rect.height),
    );
  }

  /**
   * Die Uhr laeuft hier, das Zeichnen unten.
   *
   * `update` schreibt Zustand (Massstab, Marken), `draw` liest ihn nur —
   * dieselbe Teilung wie in der Weltszene (Gate FG-17).
   */
  override update(): void {
    if (!this.scene.isVisible()) return;
    const camera = this.cameras.main;
    this.pxPer = mapScale(store.stats, camera.width, camera.height);
    this.cx = camera.width / 2;
    this.cy = camera.height / 2;
    zoneRings(store.save.era, this.rings);
    mapMarks(
      store.save, builtIds(store.save.built), this.seed, this.ship, this.course, this.marks,
    );
    this.draw();
  }

  private draw(): void {
    const g = this.gfx;
    g.clear();

    // Die Zonenringe — sie sind die Karte: „wie weit draussen bin ich".
    for (const radius of this.rings) {
      g.lineStyle(1, MAP_RING, 0.8);
      g.strokeCircle(this.cx, this.cy, radius * this.pxPer);
    }
    // Die Alphawand: kein Ring wie die anderen, sondern eine Grenze.
    const wall = balancing().world.fieldRadius * this.pxPer;
    g.lineStyle(2, MAP_WALL, 0.7);
    g.strokeCircle(this.cx, this.cy, wall);

    for (const mark of this.marks) this.drawMark(g, mark);
  }

  private drawMark(g: Phaser.GameObjects.Graphics, mark: MapMark): void {
    const px = this.cx + mark.x * this.pxPer;
    const py = this.cy + mark.y * this.pxPer;
    if (mark.kind === 'base') {
      g.fillStyle(MAP_BASE, 1);
      g.fillCircle(px, py, 5);
      g.lineStyle(1, MAP_BASE, 0.5);
      g.strokeCircle(px, py, 10);
      return;
    }
    if (mark.kind === 'ship') {
      g.fillStyle(MAP_SHIP, 1);
      g.fillCircle(px, py, 3.5);
      return;
    }
    if (mark.kind === 'planet') {
      // Ein Planet: eine Scheibe in seiner Bodenfarbe mit Umlaufbahn-Ring —
      // größer als die Kapsel und ohne deren Punkt in der Mitte.
      const farbe = parseInt((planetById(mark.id)?.surface ?? '#8c6a4a').slice(1), 16);
      g.fillStyle(farbe, 1);
      g.fillCircle(px, py, 7);
      g.lineStyle(1, farbe, 0.55);
      g.strokeCircle(px, py, 12);
      return;
    }
    if (mark.kind === 'course') {
      g.lineStyle(1.5, MAP_COURSE, 0.9);
      g.strokeCircle(px, py, 9);
      g.lineBetween(px - 12, py, px + 12, py);
      g.lineBetween(px, py - 12, px, py + 12);
      return;
    }
    // Ein offener Auftragsschritt: gefuellte Raute, damit er sich von den
    // runden Marken auch ohne Farbwissen unterscheidet (dieselbe Regel wie im
    // Technologiebaum — ein Merkmal genuegt nicht).
    g.fillStyle(MAP_SIDE, 1);
    g.beginPath();
    g.moveTo(px, py - 6); g.lineTo(px + 6, py); g.lineTo(px, py + 6); g.lineTo(px - 6, py);
    g.closePath();
    g.fillPath();
  }

  private bindInput(): void {
    // **Treffer beim Druecken**, nicht beim Bewegen: ein Finger hat keinen
    // Zustand vor dem Druecken (`oberflaeche.md`).
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!this.open) return;
      const camera = this.cameras.main;
      const x = (pointer.x - camera.x - this.cx) / this.pxPer;
      const y = (pointer.y - camera.y - this.cy) / this.pxPer;
      const treffer = markAt(this.marks, x, y, PICK_PIXELS / this.pxPer);
      if (treffer) this.callbacks.onPick(treffer);
      else this.callbacks.onPickPoint(x, y);
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!this.open) return;
      const camera = this.cameras.main;
      const x = (pointer.x - camera.x - this.cx) / this.pxPer;
      const y = (pointer.y - camera.y - this.cy) / this.pxPer;
      const treffer = markAt(this.marks, x, y, PICK_PIXELS / this.pxPer);
      this.label.setText(
        treffer ? `${treffer.label} · ${distStr(Math.hypot(treffer.x, treffer.y))}` : '',
      );
      this.label.setColor(cssColor(treffer?.kind === 'side' ? MAP_SIDE : MAP_SHIP));
    });

    // Ein geschlossenes Panel raeumt die Beschriftung ab — sie ist ein
    // Szenenobjekt und verschwindet sonst nur, wenn jemand sie abmeldet.
    bus.on('panel:close', () => this.label.setText(''));
  }
}
